import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { toast } from 'sonner';
import NavigationSidebar from './NavigationSidebar';
import TopBar from './TopBar';
import { PageHeaderProvider } from './PageHeader';
import AICommandPanel from './AICommandPanel';
import { CrispWidget } from './CrispWidget';
import SupportChatLauncher from './SupportChatLauncher';
// import { TrialBanner } from './TrialBanner'; — desativado temporariamente
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { useAuth } from '../../hooks/useAuth';
import { initChatNotifications } from '../../hooks/useChatNotifications';
import { getSupportChatState, openSupportChat } from '../../hooks/useSupportChat';
import { chatNotifySocket, type ChatNotifyPayload } from '../../lib/chatNotifySocket';
import { notificationSocket } from '../../lib/notificationSocket';
import { notificationsService, type NotificationItem } from '../../services/notifications';
import { attendancesService } from '../../services/attendances';
import {
  claimNotification,
  getNotificationPriority,
  installAudioUnlock,
  playNotificationSound,
} from '../../lib/notificationAlerts';
import { ApiError } from '../../services/api';
import {
  clearReturnTarget, getActiveCardConversation, isConversationShortcut, loadPageBeforeChat, loadReturnTarget,
  rememberPageBeforeChat, restoreLeftOpenCard, takeLeftOpenCard,
} from '../../lib/conversationBridge';
import { rememberVisit } from '../../lib/navMemory';
import OpenTabsBar from './OpenTabsBar';
import CommandPalette from './CommandPalette';
import { useScrollMemory } from '../../hooks/useScrollMemory';

export default function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 1280);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Co-piloto IA oculto temporariamente (feature ainda não pronta) — não remover.
  const [, setAIPanelOpen] = useState(false);
  const aiPanelOpen = false;
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location.pathname);
  // Rolagem da área principal lembrada por página (sair e voltar reencontra a posição).
  const mainRef = useScrollMemory(`page:${location.pathname}`, { resetWhenEmpty: true });
  // Onde a área de conteúdo começa (abaixo do topo e da barra de abertos) — o
  // card aberto (DialogContent mainArea) cobre só dali para baixo.
  useEffect(() => {
    const main = document.querySelector<HTMLElement>('main[data-app-main]');
    if (!main) return;
    const apply = () => document.documentElement.style.setProperty(
      '--app-content-top', `${Math.round(main.getBoundingClientRect().top)}px`,
    );
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(main);
    window.addEventListener('resize', apply);
    return () => { observer.disconnect(); window.removeEventListener('resize', apply); };
  }, []);

  // Alt+A — atalho do chat. Com um card aberto: abre a conversa do
  // solicitante. Em qualquer outra página: vai para o Atendimento. No
  // Atendimento: volta para o card de onde a pessoa veio ou para a última
  // página. Tratado aqui (sempre montado) e não em Conversas.tsx, que é lazy —
  // um Alt+A antes dela carregar se perderia.
  const CHAT_PATH = '/atendimento/conversas';
  // Voltou a uma página onde deixou um card aberto (por qualquer caminho:
  // barra lateral, voltar do navegador, link) → entra nela já com ?card=, no
  // render, para a página montar abrindo o card.
  // Só na chegada (o caminho mudou): dentro da página, a própria página abre e
  // fecha o card — e tira o ?card da URL antes de marcá-lo como aberto.
  const lastPathRef = useRef<string | null>(null);
  const arrived = lastPathRef.current !== location.pathname;
  const reopenCardPath = arrived ? restoreLeftOpenCard(location.pathname, location.search) : null;
  useEffect(() => {
    lastPathRef.current = location.pathname;
    if (reopenCardPath) return;
    const url = `${location.pathname}${location.search}`;
    rememberVisit(url);
    if (location.pathname !== CHAT_PATH) rememberPageBeforeChat(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isConversationShortcut(e)) return;
      e.preventDefault();
      if (locationRef.current === CHAT_PATH) {
        const target = loadReturnTarget() ?? takeLeftOpenCard();
        if (target) {
          clearReturnTarget();
          navigate(target.path);
          return;
        }
        const previous = loadPageBeforeChat();
        if (previous) navigate(previous);
        return;
      }
      const openCardConversation = getActiveCardConversation();
      if (openCardConversation) openCardConversation();
      else navigate(CHAT_PATH);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      if (window.innerWidth >= 768) setMobileSidebarOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Canal WS global de notificação de mensagem nova no chat interno — uma
  // única conexão pro app inteiro (ver chatNotifySocket.ts), disparando
  // toast (ou Notification nativa, se a aba estiver em background) quando
  // chega mensagem em um grupo que não é o que está aberto no momento.
  useEffect(() => {
    // Conta is_staff não tem escola vinculada e não usa o chat interno
    // (removido do sidebar do admin) — evita abrir uma conexão WS que
    // sempre falha pra essa conta.
    if (!user?.id || user.is_staff) return;

    const cleanupStore = initChatNotifications();

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        // silencioso — sem permissão, só o badge continua funcionando
      });
    }

    const onNotify = (payload: ChatNotifyPayload) => {
      if (payload.last_message.is_deleted) return;
      if (locationRef.current === `/chat/${payload.group_id}`) return;
      // Canal de suporte: aberto no painel flutuante = já está vendo; fechado,
      // o "Ver" abre o painel em vez de tirar a pessoa da tela atual.
      const support = getSupportChatState();
      const isSupport = support.groupId === payload.group_id;
      if (isSupport && support.open) return;
      const openGroup = () => (isSupport ? openSupportChat() : navigate(`/chat/${payload.group_id}`));

      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(`${payload.last_message.author_name} no chat interno`, {
          body: payload.last_message.content,
          tag: `sophia-chat-${payload.group_id}`,
        });
        return;
      }

      toast.message(payload.last_message.author_name, {
        description: payload.last_message.content,
        action: { label: 'Ver', onClick: openGroup },
      });
    };

    chatNotifySocket.on('chat.notify.update', onNotify);

    return () => {
      chatNotifySocket.off('chat.notify.update', onNotify);
      cleanupStore();
    };
  }, [user?.id, navigate]);

  // Canal WS global de notificação (ver notificationSocket.ts) — uma única
  // conexão pro app inteiro, disparando toast (+ som/nativa conforme a
  // prioridade, ver notificationAlerts.ts) quando uma Notification nova
  // chega para o usuário logado (ex: card criado num board do seu setor).
  // O bump do badge/lista do sino é responsabilidade de cada instância de
  // useNotifications() (TopBar, /notificacoes) — este efeito só cuida do
  // toast, mesmo padrão de separação do bloco de chat acima.
  useEffect(() => {
    // Conta is_staff não tem escola vinculada — nunca recebe este tipo de
    // notificação (sempre escopada por escola), mesma exclusão do chat.
    if (!user?.id || user.is_staff) return;

    notificationSocket.connect();

    const onNotification = (n: NotificationItem) => {
      const handleOpen = async () => {
        notificationsService.markRead(n.id).catch(() => {});
        // Atendimento precisando de humano — navega direto pra Central de
        // Atendimento; a própria página resolve o telefone/canal a partir
        // do id da sessão (?focusSessionId=, ver Conversas.tsx) porque o
        // toast pode ser clicado de QUALQUER página do app, não só de
        // dentro de Atendimentos (mesmo raciocínio de focusCreatedAttendance
        // em AtendimentoInbox.tsx, só que disparado por query param).
        // Vale também pra atendimento encaminhado/pedido de ajuda
        // (session_forwarded_to_sector) — qualquer notificação de sessão.
        if (n.entity_type === 'session' && n.entity_id) {
          navigate(`/atendimento/conversas?focusSessionId=${n.entity_id}`);
          return;
        }
        if (n.entity_type !== 'card' || !n.entity_id) {
          navigate('/notificacoes');
          return;
        }
        try {
          const { board } = await notificationsService.resolveCard(n.entity_id);
          navigate(`/board/${board}?card=${n.entity_id}`);
        } catch (err) {
          if (err instanceof ApiError && err.status === 403) {
            toast.error('Você não tem mais acesso a este card.');
          } else if (err instanceof ApiError && err.status === 404) {
            toast.error('Este card não está mais disponível.');
          } else {
            toast.error('Não foi possível abrir o card.');
          }
        }
      };

      // "Atender" direto do aviso (padrão Zendesk: o primeiro que clica fica
      // com o atendimento). Quem chega depois é avisado de quem assumiu.
      // Coordenador é só-leitura — fica só com "Ver".
      const handleAttend = async () => {
        if (!user?.id || !n.entity_id) return;
        notificationsService.markRead(n.id).catch(() => {});
        const sessionId = Number(n.entity_id);
        try {
          await attendancesService.escalateSession(sessionId, {
            operator_id: user.id,
            reason: 'Atribuído via aviso "precisa de humano"',
          });
          toast.success('Você assumiu o atendimento.');
          navigate(`/atendimento/conversas?focusSessionId=${sessionId}`);
        } catch (err) {
          if (err instanceof ApiError && err.status === 403) {
            const owner = await attendancesService.getSession(sessionId)
              .then((s) => s.agent_username)
              .catch(() => null);
            toast.info(owner ? `${owner} já assumiu este atendimento.` : 'Outra pessoa já assumiu este atendimento.');
          } else {
            toast.error(err instanceof ApiError ? err.message : 'Não foi possível assumir o atendimento.');
          }
        }
      };
      const canAttend = n.notification_type === 'session_needs_human' && !!n.entity_id
        && user?.profile?.role !== 'coordinator';

      // Prioridade decide o peso do aviso (ver notificationAlerts.ts):
      // urgente fica na tela até a pessoa agir — não some sozinho enquanto
      // ela está longe do computador.
      const priority = getNotificationPriority(n.notification_type);
      const action = canAttend
        ? { label: 'Atender', onClick: () => { handleAttend(); } }
        : { label: 'Ver', onClick: () => { handleOpen(); } };
      const cancel = canAttend ? { label: 'Ver', onClick: () => { handleOpen(); } } : undefined;
      if (priority === 'urgent') {
        toast.warning(n.title, {
          id: n.id,
          description: n.body || undefined,
          action,
          cancel,
          duration: Infinity,
          closeButton: true,
          richColors: true,
        });
      } else {
        toast.message(n.title, { id: n.id, description: n.body || undefined, action, cancel });
      }

      if (priority === 'silent') return;
      // Som e notificação nativa saem de UMA aba só (o toast aparece em todas).
      claimNotification(n.id).then((won) => {
        if (!won) return;
        playNotificationSound(priority);
        if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const native = new Notification(n.title, {
            body: n.body || undefined,
            tag: `sophia-notif-${n.id}`,
            requireInteraction: priority === 'urgent',
          });
          native.onclick = () => {
            window.focus();
            native.close();
            handleOpen();
          };
        }
      });
    };

    notificationSocket.on('notification.new', onNotification);
    const cleanupAudioUnlock = installAudioUnlock();

    return () => {
      notificationSocket.off('notification.new', onNotification);
      notificationSocket.disconnect();
      cleanupAudioUnlock();
    };
  }, [user?.id, user?.is_staff, user?.profile?.role, navigate]);

  // TODO: gate de onboarding desabilitado temporariamente (hotfix).
  // Será refeito com persistência via campo school.onboarding_complete
  // no backend, em vez de localStorage. Não remover — reativar na correção definitiva.
  //
  // useEffect(() => {
  //   const role = user?.profile?.role;
  //   const schoolId = user?.profile?.school;
  //   if (role !== 'manager' || !schoolId) return;
  //
  //   const alreadyDone = localStorage.getItem(`onboarding_done_${schoolId}`);
  //   if (alreadyDone) return;
  //
  //   whatsappService.getWhatsAppStatus().then(({ status }) => {
  //     if (status !== 'connected') {
  //       navigate('/onboarding', { replace: true });
  //     }
  //   }).catch(() => {});
  // }, []);

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-[#0a0b0d] overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Left Navigation */}
      <NavigationSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main area with resizable AI panel */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-w-0">
        {/* Main Content */}
        <ResizablePanel defaultSize={aiPanelOpen ? 75 : 100} minSize={60}>
          <PageHeaderProvider>
          <div className="relative flex flex-col h-full overflow-hidden">
            <TopBar
              onToggleAI={() => setAIPanelOpen(!aiPanelOpen)}
              aiOpen={aiPanelOpen}
              onMobileMenu={() => setMobileSidebarOpen(true)}
            />
            <OpenTabsBar />
            <CommandPalette />
            {/* <TrialBanner /> — desativado temporariamente */}
            <main ref={mainRef} data-app-main className="flex-1 overflow-auto">
              <DndProvider backend={HTML5Backend}>
                <Suspense fallback={
                  <div className="flex items-center justify-center h-full w-full py-24">
                    <div className="size-6 border-2 border-slate-300 dark:border-white/10 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin" />
                  </div>
                }>
                  {reopenCardPath ? <Navigate to={reopenCardPath} replace /> : <Outlet />}
                </Suspense>
              </DndProvider>
            </main>
            <SupportChatLauncher />
          </div>
          </PageHeaderProvider>
        </ResizablePanel>

        {/* Resizable AI Panel — max ~33% of screen */}
        {aiPanelOpen && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel
              defaultSize={25}
              minSize={18}
              maxSize={33}
              className="border-l border-slate-200 dark:border-white/[0.08]"
            >
              <AICommandPanel
                open={aiPanelOpen}
                onClose={() => setAIPanelOpen(false)}
              />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
      <CrispWidget />
    </div>
  );
}
